import {
  DefaultHttpStack,
  Upload,
  type HttpRequest,
  type HttpResponse,
  type HttpStack,
} from "tus-js-client"
import { Session } from "@supabase/supabase-js"
import { networkTimeoutError } from "./fetchWithTimeout"
import { getLiveSessionWithTimeout } from "@/lib/auth/liveSession"

/**
 * Rewrite a Location header so its origin matches the endpoint the client
 * actually used. Self-hosted Supabase Storage behind a Cloudflare Tunnel
 * (or any HTTPS-edge / HTTP-internal proxy) returns Location URLs built
 * from its own internal host:port when the storage-api isn't configured
 * to trust forwarded headers — `http://<host>:<internal-port>/...`.
 *
 * Since the same backend serves both the public HTTPS origin and the raw
 * internal one, swapping the Location's origin for the endpoint's origin
 * (and keeping the upload session id in the path) lands subsequent
 * PATCH/HEAD requests on the working route without triggering ATS.
 *
 * Returns the original value when origins already match — so production
 * stacks, which return correct URLs, are untouched.
 */
function rewriteLocationToEndpointOrigin(
  value: string | undefined,
  endpoint: string,
): string | undefined {
  if (!value) return value
  try {
    const expected = new URL(endpoint)
    const actual = new URL(value)
    if (actual.origin === expected.origin) return value
    const rewritten = new URL(
      actual.pathname + actual.search + actual.hash,
      expected.origin,
    ).toString()
    // One-line breadcrumb so it's obvious in the WebView console that the
    // rewrite is taking effect. Remove once we're confident.
    console.log("[tus] rewrote Location header", { from: value, to: rewritten })
    return rewritten
  } catch {
    return value
  }
}

/**
 * Purge any tus-js-client upload fingerprints that point at origins other
 * than the one we're currently configured to use. They accumulate when
 * earlier-session uploads were interrupted, and on dev environments they
 * commonly point at now-bogus internal URLs (http://<host>:<internal-port>).
 * Letting them stick around forces every fresh upload to first try resuming
 * a dead URL — which can hang for tus's full retry budget before falling
 * back to a clean POST.
 *
 * Runs once per module load. No-op when there's nothing to clean.
 */
function pruneStaleTusFingerprints(endpoint: string) {
  try {
    const expectedOrigin = new URL(endpoint).origin
    let removed = 0
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (!key?.startsWith("tus::")) continue
      // Keys look like: tus::tus-br--<mediaType>-<size>--<endpoint>
      // The endpoint segment (after the last `--`) tells us which origin
      // this fingerprint was created against.
      const endpointSegment = key.split("--").pop() ?? ""
      try {
        if (new URL(endpointSegment).origin !== expectedOrigin) {
          localStorage.removeItem(key)
          removed++
        }
      } catch {
        // Malformed key — safest to drop.
        localStorage.removeItem(key)
        removed++
      }
    }
    if (removed > 0) {
      console.log(`[tus] pruned ${removed} stale upload fingerprints`)
    }
  } catch {
    // localStorage unavailable (SSR, private mode, etc.) — silently skip.
  }
}

let prunedFingerprints = false

/**
 * How long each request waits for the auth client before falling back to the
 * last token it saw. Shorter than getLiveSessionWithTimeout's default: a
 * stalled refresh should not hold up every PATCH by 30s.
 */
const REQUEST_SESSION_TIMEOUT_MS = 10000

/**
 * Build a tus onBeforeRequest hook that sets the bearer token from the live
 * session on every request. tus reuses its static `headers` for each PATCH
 * and for its own retries, so a slow or resumed upload would otherwise keep
 * sending the token it started with long after that token expired.
 *
 * If no live session is available (offline, stalled refresh), the request
 * keeps the most recent token rather than failing: it may still be valid,
 * and if it isn't, Storage's rejection is classified as retryable.
 *
 * The header must only be set here, not also in `headers`: the browser stack
 * uses XHR setRequestHeader, which appends to an existing value.
 */
export function createAuthorizationRefresher(
  initialSession: Session,
  getSession: () => Promise<Session | null> = () =>
    getLiveSessionWithTimeout(REQUEST_SESSION_TIMEOUT_MS),
): (req: HttpRequest) => Promise<void> {
  let accessToken = initialSession.access_token
  return async (req) => {
    const session = await getSession().catch(() => null)
    if (session?.access_token) accessToken = session.access_token
    req.setHeader("authorization", `Bearer ${accessToken}`)
  }
}

/**
 * Wraps tus-js-client's DefaultHttpStack so every response's `Location`
 * header is normalised to the endpoint's origin. Used only in dev — see
 * the rewrite helper above for the why.
 */
class LocationRewritingHttpStack implements HttpStack {
  private inner = new DefaultHttpStack({})

  constructor(private endpoint: string) {}

  createRequest(method: string, url: string): HttpRequest {
    const req = this.inner.createRequest(method, url)
    const originalSend = req.send.bind(req)
    req.send = async (body) => {
      const res: HttpResponse = await originalSend(body)
      const originalGetHeader = res.getHeader.bind(res)
      res.getHeader = (name: string) => {
        const raw = originalGetHeader(name)
        if (name.toLowerCase() === "location") {
          return rewriteLocationToEndpointOrigin(raw, this.endpoint)
        }
        return raw
      }
      return res
    }
    return req
  }

  getName(): string {
    return "LocationRewritingHttpStack"
  }
}

/**
 * Upload a file to Supabase Storage using tus-js-client for resumable uploads.
 *
 * Dev-environment gotcha (Capacitor iOS + self-hosted Supabase behind a
 * Cloudflare Tunnel or similar HTTPS-edge / HTTP-internal proxy):
 *
 * tus follows the `Location` header returned by the POST to /upload/resumable
 * for every subsequent PATCH/HEAD. If the storage-api container isn't told to
 * trust forwarded headers, that Location comes back as
 * `http://<host>:<internal-port>/...` — and iOS App Transport Security blocks
 * the plain-HTTP follow-up, manifesting as uploads that "hang forever" while
 * tus burns through its retry delays (~38s) and the attachment queue then
 * re-queues for another pass.
 *
 * The real fix is server-side (storage-api `REQUEST_X_FORWARDED_HOST_REGEXP`
 * + a Host-preserving reverse proxy), but the supabase CLI doesn't expose
 * that knob. So in dev builds only we install a custom tus httpStack that
 * rewrites the Location header to match the endpoint's origin. Managed
 * Supabase and properly-configured prod stacks return the right URL to begin
 * with, so the rewrite is a no-op there even if it ran.
 */
export async function uploadFile(
  bucketName: string,
  fileName: string,
  file: File | Blob,
  metadata: Record<string, string>,
  session: Session,
  onProgressUpdate?: (percentageComplete: number) => void,
): Promise<void> {
  const endpoint = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/upload/resumable`

  if (!prunedFingerprints) {
    pruneStaleTusFingerprints(endpoint)
    prunedFingerprints = true
  }

  const tusOptions = {
    endpoint,
    // Always install the rewrite — it no-ops when origins already match
    // (i.e. on production / managed Supabase), so leaving it always-on
    // costs nothing there and avoids being silently inactive in builds
    // where `import.meta.env.DEV` is false (e.g. `cap sync`-bundled assets).
    httpStack: new LocationRewritingHttpStack(endpoint),
    retryDelays: [0, 3000, 5000, 10000, 20000],
    headers: {
      "x-upsert": "true",
    },
    onBeforeRequest: createAuthorizationRefresher(session),
    uploadDataDuringCreation: true,
    removeFingerprintOnSuccess: true,
    metadata: {
      bucketName: bucketName,
      objectName: fileName,
      contentType: metadata.contentType || "image/png",
      cacheControl: "3600",
      ...metadata,
    },
    chunkSize: 6 * 1024 * 1024,
  }

  return new Promise((resolve, reject) => {
    let settled = false
    let activeUpload: Upload
    let watchdog: ReturnType<typeof setTimeout>

    const finish = (error?: Error) => {
      if (settled) return
      settled = true
      clearTimeout(watchdog)
      if (error) reject(error)
      else resolve()
    }
    const resetWatchdog = () => {
      clearTimeout(watchdog)
      watchdog = setTimeout(() => {
        finish(networkTimeoutError())
        // Preserve the fingerprint so the queue can resume on its next pass.
        // Do not await abort: even broken native cleanup must release the queue.
        void activeUpload.abort().catch(() => {})
      }, 60000)
    }

    const createUpload = (canRetryFresh: boolean): Upload => {
      const upload = new Upload(file, {
        ...tusOptions,
        onProgress(bytesUploaded, bytesTotal) {
          if (settled || activeUpload !== upload) return
          resetWatchdog()
          onProgressUpdate?.((bytesUploaded / bytesTotal) * 100)
        },
        onError(error) {
          if (settled || activeUpload !== upload) return
          // A stale fingerprint can point to an upload that no longer exists.
          if (canRetryFresh && String(error).includes("failed to resume")) {
            console.warn(
              "Photo upload resume failed, retrying as fresh upload:",
              { fileName },
            )
            activeUpload = createUpload(false)
            resetWatchdog()
            activeUpload.start()
            return
          }
          finish(error)
        },
        onSuccess() {
          if (activeUpload === upload) finish()
        },
      })
      return upload
    }

    activeUpload = createUpload(true)
    resetWatchdog()
    activeUpload
      .findPreviousUploads()
      .then(
        (previousUploads) => {
          if (settled) return
          if (previousUploads.length) {
            activeUpload.resumeFromPreviousUpload(previousUploads[0])
          }
          activeUpload.start()
        },
        () => {
          if (!settled) activeUpload.start()
        },
      )
      .catch((error: unknown) => {
        finish(error instanceof Error ? error : new Error(String(error)))
      })
  })
}
